import { useState, useEffect, useRef } from 'react'
import type { AppNotification } from '@esf/shared'

interface NotificationTrayProps {
  base: string
  defaultHeaders: HeadersInit
  onNavigate?: (path: string) => void
}

const PRIORITY_COLORS = {
  URGENT: 'border-l-red-500 bg-red-50',
  HIGH: 'border-l-orange-500 bg-orange-50',
  NORMAL: 'border-l-blue-500 bg-blue-50',
  LOW: 'border-l-gray-400 bg-gray-50',
}

const TYPE_ICONS: Record<string, string> = {
  SYSTEM: '⚙️',
  ASSET_ALERT: '📦',
  WORK_ORDER: '🔧',
  IOT_ALERT: '📡',
  ESG_REPORT: '🌱',
}

const SHOWN_NOTIFICATIONS_KEY = 'esf_shown_browser_notifications'

export default function NotificationTray({ base, defaultHeaders, onNavigate }: NotificationTrayProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const pollIntervalRef = useRef<number | null>(null)

  // Load shown notifications from localStorage
  const loadShownNotifications = () => {
    try {
      const stored = localStorage.getItem(SHOWN_NOTIFICATIONS_KEY)
      return stored ? new Set(JSON.parse(stored)) : new Set()
    } catch {
      return new Set()
    }
  }
  const shownBrowserNotifications = useRef<Set<string>>(loadShownNotifications())

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const r = await fetch(`${base}/notifications`, { headers: defaultHeaders, credentials: 'include' })
      const d = await r.json()
      if (r.ok) {
        setNotifications(d.items || [])
        setUnreadCount(d.unreadCount || 0)

        // Trigger browser notification for new unread items we haven't shown yet
        const items = d.items || []
        for (const item of items) {
          if (
            item &&
            !item.read &&
            item.channels?.includes('BROWSER') &&
            !shownBrowserNotifications.current.has(item.id)
          ) {
            shownBrowserNotifications.current.add(item.id)
            // Persist to localStorage
            try {
              localStorage.setItem(
                SHOWN_NOTIFICATIONS_KEY,
                JSON.stringify(Array.from(shownBrowserNotifications.current))
              )
            } catch (e) {
              console.error('Failed to save shown notifications:', e)
            }
            showBrowserNotification(item)
            break // Only show one notification at a time
          }
        }
      }
    } catch (e) {
      console.error('Failed to fetch notifications:', e)
    }
  }

  // Browser notification
  const showBrowserNotification = async (notification: AppNotification) => {
    if (!('Notification' in window)) return

    if (Notification.permission === 'default') {
      await Notification.requestPermission()
    }

    if (Notification.permission === 'granted') {
      const n = new Notification(notification.title, {
        body: notification.message,
        icon: '/logo.svg',
        tag: notification.id,
      })

      n.onclick = () => {
        window.focus()
        if (notification.link && onNavigate) {
          onNavigate(notification.link)
        }
        setIsOpen(true)
        n.close()
      }

      // Auto-close after 5 seconds
      setTimeout(() => n.close(), 5000)
    }
  }

  // Mark as read
  const markAsRead = async (id: string) => {
    try {
      await fetch(`${base}/notifications/${id}/read`, {
        method: 'PATCH',
        headers: defaultHeaders,
        credentials: 'include'
      })
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, read: true, readAt: new Date().toISOString() } : n)
      )
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch (e) {
      console.error('Failed to mark as read:', e)
    }
  }

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await fetch(`${base}/notifications/read-all`, {
        method: 'POST',
        headers: defaultHeaders,
        credentials: 'include'
      })
      setNotifications(prev => prev.map(n => ({ ...n, read: true, readAt: new Date().toISOString() })))
      setUnreadCount(0)
    } catch (e) {
      console.error('Failed to mark all as read:', e)
    }
  }

  // Dismiss notification
  const dismiss = async (id: string) => {
    try {
      await fetch(`${base}/notifications/${id}/dismiss`, {
        method: 'PATCH',
        headers: defaultHeaders,
        credentials: 'include'
      })
      setNotifications(prev => prev.filter(n => n.id !== id))
      setUnreadCount(prev => {
        const waUnread = notifications.find(n => n.id === id && !n.read)
        return waUnread ? Math.max(0, prev - 1) : prev
      })
      // Remove from shown notifications tracking
      if (shownBrowserNotifications.current.has(id)) {
        shownBrowserNotifications.current.delete(id)
        try {
          localStorage.setItem(
            SHOWN_NOTIFICATIONS_KEY,
            JSON.stringify(Array.from(shownBrowserNotifications.current))
          )
        } catch (e) {
          console.error('Failed to update shown notifications:', e)
        }
      }
    } catch (e) {
      console.error('Failed to dismiss:', e)
    }
  }

  // Dismiss all
  const dismissAll = async () => {
    try {
      await fetch(`${base}/notifications/dismiss-all`, {
        method: 'POST',
        headers: defaultHeaders,
        credentials: 'include'
      })
      setNotifications([])
      setUnreadCount(0)
      // Clear all shown notifications tracking
      shownBrowserNotifications.current.clear()
      try {
        localStorage.removeItem(SHOWN_NOTIFICATIONS_KEY)
      } catch (e) {
        console.error('Failed to clear shown notifications:', e)
      }
    } catch (e) {
      console.error('Failed to dismiss all:', e)
    }
  }

  // Handle notification click
  const handleNotificationClick = (notification: AppNotification) => {
    if (!notification.read) {
      markAsRead(notification.id)
    }
    if (notification.link && onNavigate) {
      onNavigate(notification.link)
      setIsOpen(false)
    }
  }

  // Request browser notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      // Don't auto-request, wait for user interaction
    }
  }, [])

  // Poll for notifications
  useEffect(() => {
    fetchNotifications()

    // Poll every 15 seconds for more responsive updates
    pollIntervalRef.current = window.setInterval(fetchNotifications, 15000)

    // Also fetch when tab becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchNotifications()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [base, defaultHeaders])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Request permission button
  const requestPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission()
      if (permission === 'granted') {
        alert('Browser notifications enabled!')
      }
    }
  }

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return 'Just now'
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`
    return date.toLocaleDateString()
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        className="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notifications"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-lg shadow-lg border z-50 max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="p-3 border-b flex items-center justify-between">
            <h3 className="font-medium text-sm">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  className="text-xs text-indigo-600 hover:text-indigo-800"
                  onClick={markAllAsRead}
                >
                  Mark all read
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  className="text-xs text-gray-500 hover:text-gray-700"
                  onClick={dismissAll}
                >
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/* Browser notification permission */}
          {'Notification' in window && Notification.permission === 'default' && (
            <div className="p-2 bg-blue-50 border-b text-xs">
              <button
                className="text-blue-600 hover:text-blue-800 underline"
                onClick={requestPermission}
              >
                Enable browser notifications
              </button>
            </div>
          )}

          {/* Notification list */}
          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                No notifications
              </div>
            ) : (
              <div>
                {notifications.map(notification => (
                  <div
                    key={notification.id}
                    className={`p-3 border-b border-l-4 cursor-pointer hover:bg-gray-50 ${
                      PRIORITY_COLORS[notification.priority]
                    } ${!notification.read ? 'font-medium' : 'opacity-75'}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1 text-sm">
                          <span>{TYPE_ICONS[notification.type] || '📢'}</span>
                          <span className="truncate">{notification.title}</span>
                        </div>
                        <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {formatTime(notification.createdAt)}
                        </p>
                      </div>
                      <button
                        className="text-gray-400 hover:text-gray-600 p-1"
                        onClick={(e) => {
                          e.stopPropagation()
                          dismiss(notification.id)
                        }}
                        aria-label="Dismiss"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
